import * as React from 'react';
import {
	ComboboxMultipleSelection,
	Options,
} from '@/components/ui/comboboxMultipleSelection';
import { useFilters, useFlagReasons } from '@/hooks';
import useDeviceWidth from '@/hooks/useDeviceWidth';
import { CaseContext } from '@/context/CaseContext';

export const FlagFilter = () => {
	const FILTER_KEY = 'flagged';
	const FILTER_REASON_KEY = 'flaggedReason';
	const width = useDeviceWidth();
	const { caseDetails } = React.useContext(CaseContext);
	const { query, hasFilter, setParamSeveralFilters } = useFilters();
	const { flagReasons } = useFlagReasons(caseDetails?.id);
	const [selected, setSelected] = React.useState<string[]>([]);
	const [isOpen, setIsOpen] = React.useState(false);
	const [searchTerm, setSearchTerm] = React.useState('');

	const optionsDefined = React.useMemo(() => {
		const auxOptions: Options[] = [
			{ label: 'All Flagged', value: 'true' },
			{ label: 'All Except Flagged', value: 'false' },
		];
		flagReasons.forEach((item) =>
			auxOptions.push({
				label: item.name,
				value: item.name,
			})
		);

		if (!searchTerm) return auxOptions;

		return auxOptions.filter((option) =>
			(option.label as string).toLowerCase().includes(searchTerm.toLowerCase())
		);
	}, [searchTerm, flagReasons]);

	const handleSelectFilters = (value?: string): void => {
		if (value) {
			const newSelected = selected.includes(value)
				? selected.filter((s) => s !== value)
				: [...selected, value];

			let flaggedValues = newSelected.filter(
				(v) => v === 'true' || v === 'false'
			);
			let reasonValues = newSelected.filter(
				(v) => v !== 'true' && v !== 'false'
			);

			if (flaggedValues.includes('true') && flaggedValues.includes('false')) {
				flaggedValues = [];
				reasonValues = [];
			}

			setParamSeveralFilters([
				{
					label: FILTER_KEY,
					value: flaggedValues.length > 0 ? flaggedValues.join(',') : undefined,
				},
				{
					label: FILTER_REASON_KEY,
					value: reasonValues.length > 0 ? reasonValues.join(',') : undefined,
				},
			]);
		}
	};

	const handleClearAll = (): void => {
		setParamSeveralFilters([
			{ label: FILTER_KEY, value: undefined },
			{ label: FILTER_REASON_KEY, value: undefined },
		]);
		setSelected([]);
	};

	React.useEffect(() => {
		const queryParam = query[FILTER_KEY] as string;
		const queryParamReason = query[FILTER_REASON_KEY] as string;
		if (queryParam || queryParamReason) {
			const value =
				queryParam && queryParamReason
					? [...queryParam.split(','), ...queryParamReason.split(',')]
					: [
							...(queryParam?.split(',') ?? []),
							...(queryParamReason?.split(',') ?? []),
					  ];
			setSelected(value);
		} else {
			setSelected([]);
		}
	}, [query]);

	const handleSearch = (value: string) => {
		setSearchTerm(value);
	};

	return (
		<ComboboxMultipleSelection
			open={isOpen}
			setOpen={(value) => {
				if (width > 640) setIsOpen(value);
			}}
			hasFilter={hasFilter(FILTER_KEY) || hasFilter(FILTER_REASON_KEY)}
			buttonContentDefault={true}
			values={selected}
			labelDropdown="Flag"
			filterOptions={optionsDefined}
			handleSelectFilters={handleSelectFilters}
			handleClearAll={handleClearAll}
			classNamePopoverContent="!w-[325px]"
			classNameTrigger="h-[33px] !px-2 !w-[73px]"
			withSearch
			handleSearch={handleSearch}
		/>
	);
};
